import { Container, Point, Sprite } from "pixi.js";
import { CompleteCallback } from "@pixi/sound";
import ICreate from "../interfaces/ICreate.ts";
import IDestroy from "../interfaces/IDestroy.ts";
import InteractionDecodeOptions from "../types/InteractionDecodeOptions.ts";
import SplitText from "./SplitText.ts";
import DecodingSpace from "./DecodingSpace.ts";
import {gsap} from "gsap";
import MotionPathPlugin from "gsap/MotionPathPlugin";
import MarkSelectionButton from "./MarkSelectionButton.ts";
import DecodingCommand from "../types/DecodeCommand.ts";
import Placement from "../types/Placement.ts";
import Direction from "../types/Direction.ts";
import MarkCodes from "../marks/MarkCodes.ts";
import MarkText from "./MarkText.ts";
import MarkUtil from "../utils/MarkUtil.ts";
import InteractionDecodeIconButton from "@/components/InteractionDecodeIconButton.ts";
import {CDN} from "@/utils/config.ts";
import SoundManager from "@/managers/SoundManager.ts";
import {MarkingBundle} from "@/sections/landing/activities/marking/MarkingBundle.ts";
import DagobahModel from "@/models/DagobahModel.ts";
import SyllableSlider from "@/components/SyllableSlider.ts";
import DecodingModel from "@/models/DecodingModel.ts";
import Timer from "@/utils/Timer.ts";
import TimerEvent from "@/events/TimerEvent.ts";
import Delay from "@/utils/Delay.ts";
import {CommonSounds} from "@/utils/CommonSounds.ts";
import InteractionDecodeIconButtonGroup from "@/components/InteractionDecodeIconButtonGroup.ts";
import RhColors from "@/models/RhColors.ts";
import ActivityEvent from "@/events/ActivityEvent.ts";
import IconButton from "@/components/IconButton.ts";
import Fa from "@/utils/Fa.ts";
import MarkingEvent from "@/events/MarkingEvent.ts";

export default class InteractionDecode extends Container implements ICreate, IDestroy
{
    private BTN_PADDING_X:number = 2;
    private BTN_PADDING_Y:number = 7;
    //private CDN_PATH:string = "https://www.rhprototypes.com/assets/audio/decoding/";
    private WITHIN_BY:number = 10;

    private animationStop:Point;
    private beetle:Sprite;
    private beetleAnimation:gsap.core.Tween;
    private beetleStartX:number;
    private beetleStartY:number;
    private btnBtmY:number;
    private btnHeight:number;
    private btnTopY:number;
    private curCommandIndex:number;
    private curAnimationDuration:number;
    private curFontSize:number;
    private curMotionPath:string;
    private curMotionPathIndex:number;
    private curPosition:number;
    private curSound:any;
    private curSyllableEndIndex:number;
    private curSyllableStartIndex:number;
    private curSplitText:SplitText;
    private decodingCommands:DecodingCommand[];
    private decodingModel:DecodingModel;
    private decodingSpaceHeight:number;
    private decodingSpaces:DecodingSpace[];
    private door:Sprite;
    private hintDecodingSpace:DecodingSpace;
    private hintShown:boolean;
    private hintTimer:Timer;
    private iconBtns:InteractionDecodeIconButtonGroup[];
    private incorrectPlacementClicks:number;
    private incorrectSyllablePlacementClicks:number;
    private instructionsPlayed:boolean;
    private interactionComplete:boolean;
    private isDebug = false;
    private isMultiSyllabic:boolean;
    private isNonsense:boolean;
    private isTrigraphDGE:boolean;
    private lastClickedIconBtn:InteractionDecodeIconButtonGroup;
    private lastClickedIconBtnHasTwo:boolean;
    private lastHoveredDecodingSpace:DecodingSpace;
    private mainBg:Sprite;
    private markSelectionAttempts:number;
    private markSelectionBtns:MarkSelectionButton[];
    private markSelectionBtnsContainer:Container;
    private markSelectionOptions:string[];
    private markUtilily:MarkUtil;
    private model:DagobahModel;
    private motionPaths:any[];
    private nonSyllabicPhaseComplete:boolean;
    private options:InteractionDecodeOptions;
    private previewMarks:boolean;
    private selectionHintPlayed:boolean;
    private selectionTimer:Timer;
    private soundManager:SoundManager;
    private splitTextDefault:SplitText;
    private syllableCheckBtn:IconButton;
    private syllableIndices:number[];
    private syllableSlider:SyllableSlider;
    private syllableSliderPoints:number[];
    private totalClicks:number;
    private totalCorrectClicks:number;
    private track:Sprite;
    private track2:Sprite;
    private trigraphDGEButtons:InteractionDecodeIconButtonGroup[];
    private trigraphDGEDecodingSpaces:DecodingSpace[];
    private vines:Sprite;

    public firstInteraction:boolean;
    public interactionCompleteCallback:(correctClicks:number, totalClicks:number) => void;
    public isPrototype:boolean;

    public get code():string
    {
        return this.options.code;
    }

    public set code(value:string)
    {
        this.options.code = value;
        this.decodingModel.curDecodingString = value;
    }

    public get correct():boolean
    {
        const possible = this.decodingCommands.length;
        const score = (possible/this.totalClicks)*100;
        if(possible == 1 || possible == 2)
        {
            if(score >= 100)
            {
                return true;
            }
        }
        else if(possible == 3)
        {
            if(score >= 66)
            {
                return true;
            }
        }
        else if(possible == 4)
        {
            if(score >= 75)
            {
                return true;
            }
        }
        else if(possible >= 5)
        {
            if(score >= 80)
            {
                return true;
            }
        }
        return false;
    }

    public get word():string
    {
        return this.options.word;
    }

    public set word(value:string)
    {
        this.options.word = value;
    }

    constructor(options?:InteractionDecodeOptions, markOptions?:string[], isNonsense:boolean = false, previewMarks:boolean = false)
    {
        super();
        this.options = options;
        this.decodingModel = DecodingModel.getInstance();
        this.decodingModel.curDecodingString = this.options.code;
        if(markOptions)
        {
            this.populateMarkSelectionOptions(markOptions);
        }
        this.isNonsense = isNonsense;
        this.previewMarks = previewMarks;
        this.model = DagobahModel.getInstance();
        this._init();
    }

    private _init():void
    {
        this.incorrectPlacementClicks = 0;
        this.incorrectSyllablePlacementClicks = 0;
        this.interactionComplete = false;
        this.isPrototype = false;
        this.btnHeight = 35;
        this.curFontSize = SplitText.DECODING_DEFAULT;
        this.firstInteraction = true;
        this.hintShown = false;
        this.isTrigraphDGE = false;
        this.selectionHintPlayed = false;
        this.instructionsPlayed = false;
        this.lastClickedIconBtnHasTwo = false;
        this.markSelectionAttempts = 0;
        this.markUtilily = MarkUtil.getInstance();
        this.nonSyllabicPhaseComplete = false;
        this.soundManager = SoundManager.getInstance();
        this.totalClicks = 0;
        this.totalCorrectClicks = 0;
        gsap.registerPlugin(MotionPathPlugin);
    }

    private addIconBtnListeners(btn:InteractionDecodeIconButtonGroup, isTrigraph=false):void
    {
        if(btn.usable)
        {
            btn.onpointerdown = this.handleClick;
            if(isTrigraph)
            {
                btn.onpointerout = this.handlePointerOutTrigraph;
                btn.onpointerover = this.handlePointerOverTrigraph;
            }
            else
            {
                btn.onpointerout = this.handlePointerOut;
                btn.onpointerover = this.handlePointerOver;
            }
        }
    }

    private animateBeetleAlongPath(duration = 10, delay = 2, start = 0, end = 1, completeCallback = this.handleAnimateBeetleComplete):void
    {
        if(this.beetleAnimation)
        {
            this.beetleAnimation.resume();
        }
        else
        {
            this.beetleAnimation = gsap.to(this.beetle, {
                duration:duration,
                delay:delay,
                ease:"linear",
                motionPath:{
                    path:this.curMotionPath,
                    autoRotate:true,
                    alignOrigin:[0.5, 0.5],
                    useRadians:true,
                    start:start,
                    end:end,
                },
                onComplete:completeCallback,
                onUpdate:this.handleAnimateBeetleUpdate,
            });
        }
    }

    private animateMarkingTilesIn(delay:number = 0):void
    {
        const targetX:number = this.model.stageWidth-this.markSelectionBtnsContainer.width-32;
        gsap.to(this.markSelectionBtnsContainer, {
            delay:delay,
            duration:1,
            x:targetX,
            ease:"power4.out",
            onComplete:() =>
            {
                if(!this.selectionHintPlayed)
                {
                    this.startSelectionTimer();
                }
                this.enableMarkSelectionButtons();
            },
        });
    }

    private animateMarkingTilesOut():void
    {
        const targetX:number = this.model.stageWidth+this.markSelectionBtnsContainer.width;
        gsap.to(this.markSelectionBtnsContainer, {duration:1, x:targetX, ease:"power4.in", onComplete:this.handleAnimateMarkingTilesOutComplete});
    }

    private animationStepComplete():void
    {
        this.beetleAnimation.pause();
        this.beetle.x = this.animationStop.x;
        this.animateMarkingTilesIn();
    }

    private btnHasMark(index:number, placement:Placement, btn?:InteractionDecodeIconButtonGroup):boolean
    {
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position && this.decodingCommands[i].placement == placement)
            {
                if(btn)
                {
                    btn.markCode = this.decodingCommands[i].code;
                }
                return true;
            }
        }
        return false;
    }
    
    private captureSyllableInfo():void
    {
        const syllableStartIndex:number = this.decodingCommands[this.curCommandIndex].position;
        let syllableEndIndex:number;
        this.curCommandIndex++;
        let finished = false;
        const len = this.decodingCommands.length;
        for(let i:number = this.curCommandIndex; i < len; i++)
        {
            if(this.decodingCommands[i].code == MarkCodes.SYLLABLE_END)
            {
                syllableEndIndex = this.decodingCommands[i].position;
                finished = true;
            }
            this.curCommandIndex++;
            if(finished)
            {
                break;
            }
        }
        //debugger;
    }

    private checkForMultiSyllabic():void
    {
        this.isMultiSyllabic = this.code.indexOf("[") > -1;
        if(this.isMultiSyllabic)
        {
            this.btnBtmY = 485;
            this.btnTopY = 218;
            this.decodingSpaceHeight = 150;
            this.beetleStartX = 305;
            this.beetleStartY = 503;
        }
        else
        {
            this.btnBtmY = 600;
            this.btnTopY = 137;
            this.decodingSpaceHeight = 210;
            this.beetleStartX = 398;
            this.beetleStartY = 616;
        }
    }

    private createAndDisplayMark(code:string, targetTextField:MarkText, markAdjustPoint?:Point, fadeInDuration = 1):void
    {
        this.markUtilily.createAndDisplayMark(code, this.curSplitText, targetTextField, this.curCommandIndex, fadeInDuration, this.onMarkFadedIn, RhColors.MARK_PRIMARY, markAdjustPoint);
        this.curCommandIndex++;
    }

    private createBeetle():void
    {
        this.beetle = Sprite.from(MarkingBundle.beetle.alias);
        this.beetle.anchor.set(.5);
        this.beetle.x = this.beetleStartX;
        this.beetle.y = this.beetleStartY;
        if(this.isMultiSyllabic)
        {
            this.beetle.visible = false;
        }
        this.addChild(this.beetle);
    }

    private createBg():void
    {
        this.mainBg = Sprite.from(MarkingBundle.mainBg.alias);
        this.addChild(this.mainBg);

        this.door = Sprite.from(MarkingBundle.door.alias);
        this.door.anchor.set(0.5);
        this.door.x = this.model.centerX;
        this.door.y = this.model.centerY;
        this.addChild(this.door);

        if(this.isMultiSyllabic)
        {
            this.track = Sprite.from(MarkingBundle.track3.alias);
            this.track.anchor.set(0.5);
            this.track.x = this.model.centerX;
            this.track.y = 236;
            this.addChild(this.track);

            this.track2 = Sprite.from(MarkingBundle.track3.alias);
            this.track2.anchor.set(0.5);
            this.track2.x = this.model.centerX;
            this.track2.y = 504;
            this.addChild(this.track2);
        }
        else
        {
            this.track = Sprite.from(MarkingBundle.track1.alias);
            this.track.anchor.set(0.5);
            this.track.x = this.model.centerX;
            this.track.y = this.model.centerY;
            this.addChild(this.track);
        }

        this.vines = Sprite.from(MarkingBundle.vines.alias);
        this.addChild(this.vines);
    }

    private createDecodingSpaces(placement:Placement, direction:Direction, visible:boolean = true):void
    {
        const startX = this.curSplitText.x;
        let startY:number;
        let curSpace:DecodingSpace;
        let curCharText:MarkText;
        let needsSecondShape = false;
        let positionsMerged = 0;
        if(placement == Placement.BTM)
        {
            startY = this.btnBtmY-this.decodingSpaceHeight;
        }
        else
        {
            startY = this.btnTopY+this.btnHeight;
        }
        const len = this.curSplitText.numChars;
        for(let i = 0; i < len; i++)
        {
            curCharText = this.curSplitText.textFields[i];
            curSpace = new DecodingSpace();
            curSpace.direction = direction;
            curSpace.shapeWidth = curCharText.width-this.BTN_PADDING_X*2;
            curSpace.shapeHeight = this.decodingSpaceHeight;
            curSpace.x = startX+curCharText.x+this.BTN_PADDING_X/2;
            curSpace.y = startY;
            curSpace.index = i;
            curSpace.textPosition = i+1;
            if(placement == Placement.BTM)
            {
                curSpace.placement = Placement.BTM;
            }
            else
            {
                curSpace.placement = Placement.TOP;
            }

            if(this.btnHasMark(i+1, placement))
            {
                if(placement == Placement.BTM && this.markIsY(i+1))
                {
                    curSpace.shapeWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                    i++;
                }
                else if(placement == Placement.BTM && this.trigraphDGEFound(i+1))
                {
                    this.createTrigraphDGEDecodingSpaces(i, startY);
                    curSpace.shapeWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                    i += 2;
                }
                else
                {
                    if(placement == Placement.BTM)
                    {
                        if(this.multiBtnMarksFoundBtm(i+1))
                        {
                            if(this.twoCharMarkFoundBtm(i))
                            {
                                curSpace.secondShapeWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                needsSecondShape = true;
                                positionsMerged = 2;
                            }
                            else if(this.threeCharMarkFoundBtm(i))
                            {
                                curSpace.secondShapeWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                needsSecondShape = true;
                                positionsMerged = 3;
                            }
                        }
                        else
                        {
                            if(this.twoCharMarkFoundBtm(i))
                            {
                                curSpace.shapeWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i++;
                            }
                            else if(this.threeCharMarkFoundBtm(i))
                            {
                                curSpace.shapeWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i += 2;
                            }
                            else
                            {
                                curSpace.shapeWidth = curCharText.width-this.BTN_PADDING_X*2;
                            }
                        }
                    }
                    else if(placement == Placement.TOP)
                    {
                        if(this.multiBtnMarksFoundTop(i+1))
                        {
                            if(this.twoCharMarkFoundTop(i))
                            {
                                curSpace.secondShapeWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText, true)-this.BTN_PADDING_X*2;
                                needsSecondShape = true;
                                positionsMerged = 2;
                            }
                            else if(this.threeCharMarkFoundTop(i))
                            {
                                curSpace.secondShapeWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText, true)-this.BTN_PADDING_X*2;
                                needsSecondShape = true;
                                positionsMerged = 3;
                            }
                        }
                        else if(this.twoMarksTogetherAndOneIsMultiple(i+1, Placement.TOP))
                        {
                            curSpace.secondShapeWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                            needsSecondShape = true;
                            positionsMerged = 2;
                        }
                        else
                        {
                            if(this.twoCharMarkFoundTop(i))
                            {
                                curSpace.shapeWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i++;
                            }
                            else if(this.threeCharMarkFoundTop(i))
                            {
                                curSpace.shapeWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i += 2;
                            }
                            else
                            {
                                curSpace.shapeWidth = curCharText.width-this.BTN_PADDING_X*2;
                            }
                        }
                    }
                }
            }
            
            if(visible)
            {
                curSpace.alpha = 1;
                curSpace.visible = true;
            }
            else
            {
                curSpace.alpha = 0;
                curSpace.visible = false;
            }
            this.addChildAt(curSpace, this.getChildIndex(this.curSplitText));
            curSpace.create();
            if(needsSecondShape)
            {
                curSpace.createSecondShape(positionsMerged);
            }
            this.decodingSpaces.push(curSpace);
        }
    }

    private createIconButtons(icon:string, placement:Placement, startingId:number = 0, visible:boolean = true):void
    {
        let curBtn:InteractionDecodeIconButtonGroup;
        let curCharText:MarkText;
        let startY:number;
        if(placement == Placement.BTM)
        {
            startY = this.btnBtmY;
        }
        else
        {
            startY = this.btnTopY;
        }
        const len = this.curSplitText.numChars;
        for(let i = 0; i < len; i++)
        {
            let needsSecondBtn = false;
            let positionsMerged = 0;
            curCharText = this.curSplitText.textFields[i];
            curBtn = new InteractionDecodeIconButtonGroup();
            curBtn.label = `iconBtn_${placement}_${this.curSplitText.textFields[i].text}`;
            curBtn.iconString = icon;
            curBtn.id = startingId;
            curBtn.indices = [];
            curBtn.indices.push(i);
            curBtn.index = i;
            
            curBtn.btnWidth = curCharText.width-this.BTN_PADDING_X*2;
            curBtn.btnHeight = this.btnHeight;
            curBtn.targetTextField = curCharText;
            if(placement == Placement.BTM)
            {
                curBtn.placement = Placement.BTM;
            }
            else
            {
                curBtn.placement = Placement.TOP;
            }
            curBtn.x = this.curSplitText.x+curCharText.x+this.BTN_PADDING_X/2;
            curBtn.y = startY;
            
            /*if(i == 1 || i == 4 || i ==5)
            {
                debugger;
            }*/
            if(this.btnHasMark(i+1, placement, curBtn))
            {
                if(placement == Placement.TOP && (this.markIsJG(i+1) || this.markIsSG(i+1)))
                {
                    curBtn.hasDoubleSelection = true;
                }
                if(placement == Placement.BTM && this.markIsY(i+1))
                {
                    curBtn.hasDoubleSelection = true;
                    curBtn.btnWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                    i++;
                    curBtn.indices.push(i);
                }
                else if(placement == Placement.BTM && this.trigraphDGEFound(i+1))
                {
                    curBtn.startsTrigraphDGE = true;
                    this.isTrigraphDGE = true;
                    this.createTrigraphDGEButtons(i, icon, startingId, startY);
                    curBtn.btnWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                    i += 2;
                    curBtn.indices.push(i);
                }
                else
                {
                    if(placement == Placement.BTM)
                    {
                        //debugger;
                        if(this.multiBtnMarksFoundBtm(i+1))
                        {
                            if(this.twoCharMarkFoundBtm(i))
                            {
                                curBtn.secondBtnWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                curBtn.indices.push(i+1);
                                needsSecondBtn = true;
                                positionsMerged = 2;
                            }
                            else if(this.threeCharMarkFoundBtm(i))
                            {
                                curBtn.secondBtnWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                curBtn.indices.push(i+2);
                                needsSecondBtn = true;
                                positionsMerged = 3;
                            }
                        }
                        else
                        {
                            if(this.twoCharMarkFoundBtm(i))
                            {
                                curBtn.btnWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i++;
                                curBtn.indices.push(i);
                            }
                            else if(this.threeCharMarkFoundBtm(i))
                            {
                                curBtn.btnWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i += 2;
                                curBtn.indices.push(i);
                            }
                            else
                            {
                                curBtn.btnWidth = curCharText.width-this.BTN_PADDING_X*2;
                            }
                        }
                    }
                    else if(placement == Placement.TOP)
                    {
                        if(this.multiBtnMarksFoundTop(i+1))
                        {
                            if(this.twoCharMarkFoundTop(i))
                            {
                                curBtn.secondBtnWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                curBtn.indices.push(i+1);
                                needsSecondBtn = true;
                                positionsMerged = 2;
                            }
                            else if(this.threeCharMarkFoundTop(i))
                            {
                                curBtn.secondBtnWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                curBtn.indices.push(i+2);
                                needsSecondBtn = true;
                                positionsMerged = 3;
                            }
                        }
                        else if(this.twoMarksTogetherAndOneIsMultiple(i+1, Placement.TOP))
                        {
                            curBtn.secondBtnWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                            curBtn.indices.push(i+1);
                            needsSecondBtn = true;
                            positionsMerged = 2;
                        }
                        else
                        {
                            if(this.twoCharMarkFoundTop(i))
                            {
                                curBtn.btnWidth = this.markUtilily.getTwoLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i++;
                                curBtn.indices.push(i);
                            }
                            else if(this.threeCharMarkFoundTop(i))
                            {
                                curBtn.btnWidth = this.markUtilily.getThreeLetterWidth(this.curSplitText, curCharText)-this.BTN_PADDING_X*2;
                                i += 2;
                                curBtn.indices.push(i);
                            }
                            else
                            {
                                curBtn.btnWidth = curCharText.width-this.BTN_PADDING_X*2;
                            }
                        }
                    }
                }
            }
            
            if(visible)
            {
                curBtn.alpha = 1;
                curBtn.visible = true;
            }
            else
            {
                curBtn.alpha = 0;
                curBtn.visible = false;
            }
            this.addChild(curBtn);
            curBtn.create();
            if(needsSecondBtn)
            {
                curBtn.createSecondButton(positionsMerged);
            }
            this.addIconBtnListeners(curBtn);
            this.iconBtns.push(curBtn);
            startingId++;
        }
    }

    private createMarkSelectionBtns():void
    {
        const btnPadding:number = 32;
        this.markSelectionBtnsContainer = new Container();
        this.markSelectionBtns = [];
        let btnNumbers:number[] = [1, 2, 3, 4];
        let len = btnNumbers.length;
        let curBtn:MarkSelectionButton;
        btnNumbers = this.randomizeArray(btnNumbers);
        for(let i:number = 0; i < len; i++)
        {
            curBtn = new MarkSelectionButton(this.markSelectionOptions[i], `tile${btnNumbers[i]}`);
            if(i > 0)
            {
                curBtn.y = this.markSelectionBtns[i-1].y+this.markSelectionBtns[i-1].height+btnPadding;
            }
            curBtn.create();
            curBtn.onpointerdown = this.handleMarkSelectionBtnClick;
            this.markSelectionBtns.push(curBtn);
            this.markSelectionBtnsContainer.addChild(curBtn);
        }
        this.markSelectionBtnsContainer.x = this.model.stageWidth;
        this.markSelectionBtnsContainer.y = this.model.centerY-this.markSelectionBtnsContainer.height/2;
        this.addChild(this.markSelectionBtnsContainer);
    }

    private createMotionPaths():void
    {
        this.motionPaths = [];
        this.motionPaths.push("M400.5,616h561.783c53.416,0,96.717-43.302,96.717-96.717V250.717c0-53.416-43.302-96.717-96.717-96.717H399.717C346.302,154,303,197.302,303,250.717v268.565c0,53.133,42.844,96.258,95.869,96.714");
        this.motionPaths.push("M400.5,616h564.783c53.416,0,96.717-43.302,96.717-96.717l1-160.565c0-53.416-43.302-96.717-96.717-96.717H400.717C347.302,262,304,305.302,304,358.717l-1,160.565c0,53.133,42.844,96.258,95.869,96.714");
        this.motionPaths.push([{x:this.beetleStartX, y:this.beetleStartY}, {x:1061, y:this.beetleStartY}]);
        if(this.isMultiSyllabic)
        {
            this.curAnimationDuration = 2;
            this.curMotionPathIndex = 2;
        }
        else
        {
            this.curAnimationDuration = 6;
            this.curMotionPathIndex = 0;
        }
        this.curMotionPath = this.motionPaths[this.curMotionPathIndex];
    }

    private createSampleMark(code:string, targetTextField:MarkText = null):void
    {
        //debugger;
        const mark = this.markUtilily.createMark(code, this.curSplitText, targetTextField);
        mark.create();
        mark.pivot.set(mark.markWidth/2, mark.markHeight/2);
        mark.x = this.model.centerX;
        mark.y = 60;
        if(mark.parentTarget)
        {
            mark.parentTarget.addChild(mark);
        }
        else
        {
            this.addChild(mark);
        }
    }
    
    private createNonSenseMark():void
    {
        let nonsenseMark = Sprite.from(MarkingBundle.nonsense.alias);
        nonsenseMark.anchor.set(0.5);
        nonsenseMark.x = this.splitTextDefault.x-((nonsenseMark.width/2)+20);
        nonsenseMark.y = this.splitTextDefault.y+this.splitTextDefault.height/2;
        this.addChild(nonsenseMark);
    }

    private createSampleMarkSelectionBtn(code:string):void
    {
        //debugger;
        const markBtn = new MarkSelectionButton(code, `tile1`);
        markBtn.create();
        markBtn.x = 50;
        markBtn.y = 50;
        this.addChild(markBtn);
    }

    private createSyllableCheckBtn():void
    {
        this.syllableCheckBtn = new IconButton(Fa.solid.check);
        this.syllableCheckBtn.bgColor = RhColors.BTN_PRIMARY;
        this.syllableCheckBtn.iconColor = RhColors.BTN_SECONDARY;
        this.syllableCheckBtn.x = this.model.centerX;
        this.syllableCheckBtn.y = 610;
        this.syllableCheckBtn.onpointerdown = this.handleSyllableCheckBtnClick;
        this.addChild(this.syllableCheckBtn);
        this.syllableCheckBtn.create();
        this.syllableCheckBtn.pivot.set(this.syllableCheckBtn.width/2, this.syllableCheckBtn.height/2);
        this.syllableCheckBtn.disable();
        this.syllableCheckBtn.visible = false;
    }

    private createSyllableSlider():void
    {
        this.syllableSlider = new SyllableSlider(this.code, this.syllableSliderPoints);
        this.syllableSlider.on(MarkingEvent.SLIDER_STEP_SET, this.handleSliderStepSet);
        this.syllableSlider.on(MarkingEvent.SLIDER_STEP_NOT_SET, this.handleSliderStepNotSet);
        this.addChild(this.syllableSlider);
        this.syllableSlider.create();
        this.syllableSlider.x = this.model.centerX;
        this.syllableSlider.y = 504;
        this.syllableSlider.visible = false;
    }
    
    private createTrigraphDGEButtons(index:number, icon:string, startingId:number, startY:number):void
    {
        //debugger;
        this.trigraphDGEButtons = [];
        const len = 3;
        for(let i = 0; i < len; i++)
        {
            const btn:InteractionDecodeIconButtonGroup = new InteractionDecodeIconButtonGroup();
            const charText = this.curSplitText.textFields[index+i];
            btn.label = `trigraphBtn_${charText.text}`;
            btn.iconString = icon;
            btn.id = i;
            btn.indices = [];
            btn.indices.push(index);
            btn.index = index+i;
            btn.isPartOfTrigraphDGE = true;
            btn.placement = Placement.BTM;
            btn.btnWidth = charText.width-this.BTN_PADDING_X*2;
            btn.btnHeight = this.btnHeight;
            btn.targetTextField = charText;
            btn.visible = false;
            this.addChild(btn);
            btn.create();
            btn.x = this.curSplitText.x+charText.x+this.BTN_PADDING_X/2;
            btn.y = startY;
            this.trigraphDGEButtons.push(btn);
            this.addIconBtnListeners(btn, true);
        }
    }
    
    private createTrigraphDGEDecodingSpaces(index:number,startY:number):void
    {
        //debugger;
        this.trigraphDGEDecodingSpaces = [];
        const len = 3;
        for(let i = 0; i < len; i++)
        {
            const space:DecodingSpace = new DecodingSpace();
            const charText:MarkText = this.curSplitText.textFields[index+i];
            space.direction = Direction.UP;
            space.placement = Placement.BTM;
            space.shapeWidth = charText.width-this.BTN_PADDING_X*2;
            space.shapeHeight = this.decodingSpaceHeight;
            space.x = this.curSplitText.x+charText.x+this.BTN_PADDING_X/2;
            space.y = startY;
            space.index = i;
            space.textPosition = index+i+1;
            space.shapeWidth = charText.width-this.BTN_PADDING_X*2;
            space.visible = false;
            this.addChildAt(space, this.getChildIndex(this.curSplitText));
            space.create();
            this.trigraphDGEDecodingSpaces.push(space);
        }
    }

    private createWord(fontSize?:number):void
    {
        this.splitTextDefault = new SplitText(this.word);
        if(this.isMultiSyllabic)
        {
            this.splitTextDefault.fontSize = SplitText.DECODING_MULTI_SYLLABIC;
        }
        if(fontSize)
        {
            this.splitTextDefault.fontSize = fontSize;
        }
        this.addChild(this.splitTextDefault);
        this.splitTextDefault.create();
        this.splitTextDefault.x = this.model.centerX-this.splitTextDefault.width/2;
        if(this.isMultiSyllabic)
        {
            this.splitTextDefault.y = 278;
        }
        else
        {
            this.splitTextDefault.y = this.model.centerY-this.splitTextDefault.height/2;
        }
        this.curSplitText = this.splitTextDefault;

        
        // Filter code to try and match mockup
        /*const maskTextStyle = {fill:0x000000, fontSize:this.splitTextDefault.fontSize, fontFamily:DecodingModel.getInstance().fontFamily};
        const maskText = new Text({text:this.word, style:maskTextStyle});
        maskText.x = this.splitTextDefault.x+1;
        maskText.y = this.splitTextDefault.y+2;
        this.addChild(maskText);
        this.addChild(this.splitTextDefault);

        const blurFilter = new BlurFilter({quality:2, strength:2});
        const dropShadowFilter = new DropShadowFilter({alpha:.5, color:"black", shadowOnly:true});
        const outlineFilter = new OutlineFilter({color:0x000000, thickness:2, knockout:true});
        const shadowText = new SplitText(this.word);
        shadowText.fontSize = this.splitTextDefault.fontSize;
        shadowText.x = this.splitTextDefault.x+1;
        shadowText.y = this.splitTextDefault.y+2;
        shadowText.filters = [outlineFilter, blurFilter];
        //shadowText.mask = maskText;
        this.addChild(shadowText);
        shadowText.create();*/
        
        
        if(this.splitTextDefault.width > 585)
        {
            this.removeChild(this.splitTextDefault);
            this.curFontSize--;
            this.createWord(this.curFontSize);
        }
    }

    private destroyBeetleAnimation():void
    {
        if(this.beetleAnimation)
        {
            this.beetleAnimation.kill();
            this.beetleAnimation = null;
        }
        
    }
    
    private destroyDecodingSpaces():void
    {
        if(this.decodingSpaces)
        {
            let len = this.decodingSpaces.length;
            for(let i = 0; i < len; i++)
            {
                this.decodingSpaces[i].destroy();
            }
            this.decodingSpaces = null;
        }
    }

    private destroyIconButtons():void
    {
        if(this.iconBtns)
        {
            let len = this.iconBtns.length;
            for(let i = 0; i < len; i++)
            {
                this.removeIconBtnListeners(this.iconBtns[i]);
                this.iconBtns[i].destroy();
            }
            this.iconBtns = null;
        }
    }

    private destroyMarkSelectionBtns():void
    {
        if(this.markSelectionBtns)
        {
            let len = this.markSelectionBtns.length;
            for(let i = 0; i < len; i++)
            {
                this.markSelectionBtns[i].destroy();
            }
            this.markSelectionBtns = null;
        }
        if(this.markSelectionBtnsContainer)
        {
            this.removeChild(this.markSelectionBtnsContainer);
            this.markSelectionBtnsContainer.destroy();
            this.markSelectionBtnsContainer = null;
        }
    }
    
    private destroyTimers():void
    {
        if(this.hintTimer)
        {
            this.hintTimer.addCustomEventListener(TimerEvent.TIMER_COMPLETE, this.playFirstHintAudio);
            this.hintTimer.reset();
            this.hintTimer = null;
        }
        if(this.selectionTimer)
        {
            this.selectionTimer.addCustomEventListener(TimerEvent.TIMER_COMPLETE, this.playFirstHintAudio);
            this.selectionTimer.reset();
            this.selectionTimer = null;
        }
    }

    private disableDecodingSpaces():void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            this.decodingSpaces[i].alpha = 0.5;
        }
    }

    private disableIconButtons():void
    {
        let len = this.iconBtns.length;
        for(let i = 0; i < len; i++)
        {
            this.removeIconBtnListeners(this.iconBtns[i]);
            this.iconBtns[i].disable();
        }
    }

    private disableMarkSelectionButtons():void
    {
        let len = this.markSelectionBtns.length;
        for(let i = 0; i < len; i++)
        {
            this.markSelectionBtns[i].onpointerdown = null;
            this.markSelectionBtns[i].disable();
        }
    }

    private disableTextFields():void
    {
        const len = this.curSplitText.textFields.length;
        for(let i:number=0; i<len; i++)
        {
            this.curSplitText.textFields[i].alpha = 0.5;
        }
    }

    private doInitialCheckForProperCodeFormat():void
    {
        if(this.options.code.length > 2)
        {
            if(this.options.code.indexOf(",") == -1)
            {
                alert("It appears that the code you entered is not formatted properly. Make sure that you have a comma between each mark code. Ex: 2x,3g,4l");
            }
        }
    }

    private enableApplicableIconButtons():void
    {
        let len = this.iconBtns.length;
        for(let i = 0; i < len; i++)
        {
            if(this.iconBtns[i].placement == Placement.TOP)
            {
                if(this.iconBtns[i].index >= this.curSyllableStartIndex && this.iconBtns[i].index < this.curSyllableEndIndex)
                {
                    this.addIconBtnListeners(this.iconBtns[i]);
                    this.iconBtns[i].enable();
                    this.curSplitText.textFields[this.iconBtns[i].index].alpha = 1;
                    this.fullAlphaTopDecodingSpace(this.iconBtns[i].index);
                }
            }
        }
    }

    private enableIconButtons():void
    {
        let len = this.iconBtns.length;
        for(let i = 0; i < len; i++)
        {
            if(this.iconBtns[i].alpha == 1)
            {
                this.addIconBtnListeners(this.iconBtns[i]);
                this.iconBtns[i].enable();
            }
        }
    }

    private enableMarkSelectionButtons():void
    {
        let len = this.markSelectionBtns.length;
        for(let i = 0; i < len; i++)
        {
            this.markSelectionBtns[i].onpointerdown = this.handleMarkSelectionBtnClick;
            this.markSelectionBtns[i].enable();
        }
    }

    private fadeInDecodingSpacesTop(toAlpha=1):void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingSpaces[i].placement == Placement.TOP)
            {
                this.decodingSpaces[i].alpha = 0;
                this.decodingSpaces[i].visible = true;
                gsap.to(this.decodingSpaces[i], {alpha:toAlpha, duration:1});
            }
        }
    }

    private fadeInAllTextFields():void
    {
        const len = this.curSplitText.textFields.length;
        for(let i:number=0; i<len; i++)
        {
            this.curSplitText.textFields[i].alpha = 1;
        }
    }

    private fadeInIconButtonsTop():void
    {
        let len = this.iconBtns.length;
        for(let i = 0; i < len; i++)
        {
            if(this.iconBtns[i].placement == Placement.TOP)
            {
                this.iconBtns[i].alpha = 0;
                this.iconBtns[i].visible = true;
                gsap.to(this.iconBtns[i], {alpha:1, duration:1});
            }
        }
    }

    private fadeInSyllabicCheckBtn():void
    {
        this.syllableCheckBtn.alpha = 0;
        this.syllableCheckBtn.visible = true;
        gsap.to(this.syllableCheckBtn, {alpha:1, delay:1, duration:1});
    }

    private fadeInSyllabicSlider():void
    {
        this.syllableSlider.alpha = 0;
        this.syllableSlider.visible = true;
        gsap.to(this.syllableSlider, {alpha:1, delay:1, duration:1, onComplete:()=>{}});
    }

    private fadeOutBug():void
    {
        gsap.to(this.beetle, {alpha:0, duration:1, onComplete:()=>{this.beetle.visible = false;}});
    }

    private fadeOutDecodingSpaces():void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            gsap.to(this.decodingSpaces[i], {alpha:0, duration:1, onComplete:()=>{this.decodingSpaces[i].visible = false;}});
        }
    }

    private fadeOutIconButtons():void
    {
        let len = this.iconBtns.length;
        for(let i = 0; i < len; i++)
        {
            gsap.to(this.iconBtns[i], {alpha:0, duration:1, onComplete:()=>{this.iconBtns[i].visible = false;}});
        }
    }

    private fadeOutSyllableSliderStuff():void
    {
        gsap.to(this.syllableSlider, {alpha:0, duration:1, onComplete:()=>{this.syllableSlider.visible = false;}});
        gsap.to(this.syllableCheckBtn, {alpha:0, duration:1, onComplete:()=>{this.syllableCheckBtn.visible = false;}});
    }

    private getCorrectDecodingSpace():DecodingSpace
    {
        let spacesTarget = this.decodingSpaces;
        if(this.isTrigraphDGE && this.trigraphDGEButtons && this.trigraphDGEButtons[0].visible)
        {
            spacesTarget = this.trigraphDGEDecodingSpaces;
        }
        const len = spacesTarget.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingCommands[this.curCommandIndex].position == spacesTarget[i].textPosition && this.decodingCommands[this.curCommandIndex].placement == spacesTarget[i].placement)
            {
                return spacesTarget[i];
            }
        }
    }

    private fullAlphaTopDecodingSpace(index:number):void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingSpaces[i].placement == Placement.TOP && this.decodingSpaces[i].index == index)
            {
                this.decodingSpaces[i].alpha = 1;
            }
        }
    }

    private getCorrectMarkSelectionBtn(code:string):MarkSelectionButton
    {
        const len = this.markSelectionBtns.length;
        for(let i = 0; i < len; i++)
        {
            if(this.markSelectionBtns[i].markCode == code)
            {
                return this.markSelectionBtns[i];
            }
        }
    }
    
    private getNextCorrectIconBtn(lastAttempt=false):InteractionDecodeIconButtonGroup
    {
        //debugger;
        let btnsTarget = this.iconBtns;
        if(this.isTrigraphDGE && this.trigraphDGEButtons && this.trigraphDGEButtons[0].visible && lastAttempt)
        {
            this.isTrigraphDGE = false;
            btnsTarget = this.trigraphDGEButtons;
        }
        const len = btnsTarget.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingCommands[this.curCommandIndex].position == btnsTarget[i].index+1 && this.decodingCommands[this.curCommandIndex].placement == btnsTarget[i].placement)
            {
                return btnsTarget[i];
            }
        }
        return this.lastClickedIconBtn;
    }

    private handleAnimateBeetleComplete = ():void =>
    {
        if(this.nonSyllabicPhaseComplete == true)
        {
            this.fadeOutBug();
            this.fadeOutDecodingSpaces();
            this.fadeOutIconButtons();
            this.fadeInSyllabicSlider();
            this.fadeInSyllabicCheckBtn();
        }
        else
        {
            this.playSound("marking_read_the_word", this.handleNowReadAudioComplete);
        }
    };

    private handleAnimateBeetleUpdate = ():void =>
    {
        //debugger;
        if(this.interactionComplete || this.nonSyllabicPhaseComplete)
        {
            return;
        }
        if(this.animationStop.y > this.model.centerY)
        {
            if(this.beetle.x >= this.animationStop.x && this.withinBy(this.beetle.y, this.animationStop.y))
            {
                this.animationStepComplete();
            }
        }
        else
        {
            if(this.beetle.x <= this.animationStop.x && this.withinBy(this.beetle.y, this.animationStop.y))
            {
                this.animationStepComplete();
            }
        }
    };

    private handleAnimateMarkingTilesOutComplete = ():void =>
    {
        if(this.lastClickedIconBtn.hasDoubleSelection)
        {
            this.lastClickedIconBtn.hasDoubleSelection = false;
            Delay.callLater(()=> {
                this.playSound("marking_what_other_mark");
                this.animateMarkingTilesIn();
            }, 500);
            return;
        }
        else if(this.lastClickedIconBtn.startsTrigraphDGE)
        {
            this.setupTrigraphDGEPhaseTwo();
            this.nextButtonUp();
            this.resetDecodingSpaces();
            return;
        }
        if(this.curCommandIndex >= this.decodingCommands.length)
        {
            this.interactionComplete = true;
            this.beetleAnimation.resume();
        }
        else
        {
            if(this.lastClickedIconBtnHasTwo && !this.lastClickedIconBtn.secondBtnShown)
            {
                this.showSecondIconBtn();
            }
            else
            {
                this.nextButtonUp();
            }
            if(this.decodingCommands[this.curCommandIndex].code != MarkCodes.SYLLABLE_BEGIN && !this.nonSyllabicPhaseComplete)
            {
                this.enableIconButtons();
                this.resetDecodingSpaces();
            }
        }
    };

    private handleClick = (event):void =>
    {
        if(this.previewMarks)
        {
            return;
        }
        this.emit(ActivityEvent.DISABLE_REPLAY_AUDIO_BTN);
        this.stopHintTimer();
        this.incorrectPlacementClicks++;
        this.soundManager.stopLastSound();
        this.lastClickedIconBtn = event.currentTarget;
        //console.log(`lastClicked.label: ${this.lastClickedIconBtn.label}`);
        this.lastClickedIconBtnHasTwo = this.lastClickedIconBtn.hasTwoButtons;
        const placement:string = this.lastClickedIconBtn.placement;
        const position:number = event.currentTarget.index+1;
        
        //console.log("incorrectPlacementClicks: ", this.incorrectPlacementClicks);
        if(position == this.decodingCommands[this.curCommandIndex].position && placement == this.decodingCommands[this.curCommandIndex].placement)
        {
            this.incorrectPlacementClicks = 0;
            this.lastClickedIconBtn.hasTwoButtons = false;
            this.disableIconButtons();
            this.lastClickedIconBtn.reset();
            this.decodingSpaces[this.lastClickedIconBtn.id].reset();
            if(this.nonSyllabicPhaseComplete)
            {
                this.animateMarkingTilesIn();
            }
            else
            {
                const target:InteractionDecodeIconButton = event.currentTarget;
                this.animationStop = new Point();
                this.animationStop.x = target.x+target.width/2;
                this.animationStop.y = target.y+target.height/2;
                let animationStart:number = 0;
                if(this.beetleAnimation)
                {
                    animationStart = this.beetleAnimation.progress();
                }
                this.animateBeetleAlongPath(this.curAnimationDuration, 0, animationStart, 1);
                if(this.lastClickedIconBtn.isPartOfTrigraphDGE)
                {
                    this.isTrigraphDGE = false;
                }
            }
        }
        else
        {
            if(this.incorrectPlacementClicks < 2)
            {
                this.playIncorrectPlacementSound();
            }
            else if(this.incorrectPlacementClicks == 2)
            {
                this.playIncorrectPlacementSound();
                this.showCorrectScaffold();
            }
            else
            {
                const target:InteractionDecodeIconButtonGroup = this.getNextCorrectIconBtn(true);
                this.playSound("marking_this_is_correct_space");
                this.incorrectPlacementClicks = 0;
                this.disableIconButtons();
                this.lastClickedIconBtn.reset();
                this.decodingSpaces[this.lastClickedIconBtn.id].reset();
                this.lastClickedIconBtn = target;
                this.lastClickedIconBtnHasTwo = target.hasTwoButtons;
                target.hasTwoButtons = false;
                this.animationStop = new Point();
                this.animationStop.x = target.x+target.width/2;
                this.animationStop.y = target.y+target.height/2;
                let animationStart:number = 0;
                if(this.beetleAnimation)
                {
                    animationStart = this.beetleAnimation.progress();
                }
                this.animateBeetleAlongPath(this.curAnimationDuration, 0, animationStart, 1);
                if(this.lastClickedIconBtn.isPartOfTrigraphDGE)
                {
                    this.isTrigraphDGE = false;
                }
            }
        }
    };

    private handleMarkSelectionBtnClick = (event:any):void =>
    {
        //debugger;
        this.markSelectionAttempts++;
        this.stopSelectionTimer();
        const target:MarkSelectionButton = event.currentTarget as MarkSelectionButton;
        this.totalClicks++;
        if(target.markCode == this.decodingCommands[this.curCommandIndex].code)
        {
            if(!this.isPrototype)
            {
                this.soundManager.playSound(CommonSounds.button_click_1);
            }
            this.hideHint();
            this.totalCorrectClicks++;
            this.disableMarkSelectionButtons();
            this.animateMarkingTilesOut();
            if(this.specialVowelFound(target.markCode, this.lastClickedIconBtn.index))
            {
                this.createAndDisplayMark(target.markCode, this.lastClickedIconBtn.targetTextField, new Point(0, 22));
            }
            else
            {
                this.createAndDisplayMark(target.markCode, this.lastClickedIconBtn.targetTextField);
            }
            if(this.isMultiSyllabic && this.nonSyllabicPhaseComplete)
            {
                if(this.curCommandIndex >= this.decodingCommands.length)
                {
                    this.fadeInAllTextFields();
                    this.fadeOutIconButtons();
                    this.fadeOutDecodingSpaces();
                    this.playSound("marking_read_the_word", this.handleNowReadAudioComplete);
                    return;
                }
                if(this.nextMarkIsSyllableBegin())
                {
                    this.fadeOutIconButtons();
                    this.fadeOutDecodingSpaces();
                    this.fadeInAllTextFields();
                    this.fadeInSyllabicSlider();
                    this.fadeInSyllabicCheckBtn();
                }
                else
                {
                    this.disableIconButtons();
                    this.disableDecodingSpaces();
                    if(this.curCommandIndex < this.decodingCommands.length-1)
                    {

                    }
                    else
                    {
                        const len = this.decodingCommands.length;
                        for(let i:number=len-1; i>=0; i--)
                        {
                            if(this.decodingCommands[i].code == MarkCodes.SYLLABLE_END)
                            {
                                this.curSyllableStartIndex = this.decodingCommands[i].position;
                                break;
                            }
                        }
                        this.curSyllableEndIndex = this.curSplitText.textFields.length;
                    }
                    Delay.callLater(()=>
                    {
                        this.disableTextFields();
                        this.enableApplicableIconButtons();
                    }, 1100);
                }
            }
        }
        else
        {
            if(this.markSelectionAttempts == 2)
            {
                this.playSound("marking_this_is_correct_mark");
                target.hide();
                setTimeout(() =>
                {
                    this.getCorrectMarkSelectionBtn(this.decodingCommands[this.curCommandIndex].code).outlined = true;
                }, 1000);

                return;
            }
            this.playSound("marking_try_again");
            target.hide();
        }
    };

    private handleMoveBugComplete = ():void =>
    {
        this.animateMarkingTilesIn();
    };
    
    private handleNowReadAudioComplete = ():void =>
    {
        //debugger;
        if(this.isPrototype)
        {
            this.handleWordAudioComplete();
            return;
        }
        let files = [`${CDN}audio/student/words/${this.options.wordAudioId}.mp3`, `${CDN}audio/student/blank0.5.mp3`, `${CDN}audio/student/words/${this.options.wordAudioId}.mp3`];
        if(this.options.wordAudioId == undefined)
        {
            files = [`${CDN}audio/student/blank0.5.mp3`, `${CDN}audio/student/blank0.5.mp3`, `${CDN}audio/student/blank0.5.mp3`];
        }
        setTimeout(() =>
        {
            this.soundManager.playSoundsInSequence(files, this.handleWordAudioComplete);
        },
        1000);
    }

    private handlePointerOut = (event):void =>
    {
        const target:InteractionDecodeIconButton = event.currentTarget;
        target.pointerOut();
        this.decodingSpaces[target.id].hideHover();
        this.lastHoveredDecodingSpace = null;
    };
    
    private handlePointerOutTrigraph = (event):void =>
    {
        const target:InteractionDecodeIconButton = event.currentTarget;
        target.pointerOut();
        this.trigraphDGEDecodingSpaces[target.id].hideHover();
        this.lastHoveredDecodingSpace = null;
    };

    private handlePointerOver = (event):void =>
    {
        const target:InteractionDecodeIconButton = event.currentTarget;
        target.pointerOver();
        this.decodingSpaces[target.id].showHover();
        this.lastHoveredDecodingSpace = this.decodingSpaces[target.id];
    };
    
    private handlePointerOverTrigraph = (event):void =>
    {
        //debugger;
        const target:InteractionDecodeIconButton = event.currentTarget;
        target.pointerOver();
        this.trigraphDGEDecodingSpaces[target.id].showHover();
        this.lastHoveredDecodingSpace = this.trigraphDGEDecodingSpaces[target.id];
    };

    private handleSliderStepNotSet = ():void =>
    {
        if(this.syllableCheckBtn)
        {
            this.syllableCheckBtn.disable();
        }
    }

    private handleSliderStepSet = ():void =>
    {
        if(this.syllableCheckBtn)
        {
            this.syllableCheckBtn.enable();
        }
    }

    private handleSyllableCheckBtnClick = ():void =>
    {
        this.incorrectSyllablePlacementClicks++;
        const sliderPosition = this.syllableSlider.curPositionIndex;
        const correctPosition = this.decodingCommands[this.curCommandIndex+1].position;
        if(sliderPosition == correctPosition)
        {
            this.incorrectSyllablePlacementClicks = 0;
            this.playSound("chime");
            this.createAndDisplayMark(MarkCodes.SYLLABLE_SPLIT, this.splitTextDefault.textFields[correctPosition-1]);
            this.fadeOutSyllableSliderStuff();
            this.fadeInDecodingSpacesTop(.5);
            this.fadeInIconButtonsTop();
            this.curSyllableStartIndex = this.decodingCommands[this.curCommandIndex-1].position;
            this.curSyllableEndIndex = this.decodingCommands[this.curCommandIndex].position;
            Delay.callLater(()=>
            {
                this.disableTextFields();
                this.enableApplicableIconButtons();
            }, 1100);
            this.curCommandIndex++;
        }
        else
        {
            if(this.incorrectSyllablePlacementClicks < 2)
            {
                this.playIncorrectPlacementSound();
            }
            else if(this.incorrectSyllablePlacementClicks == 2)
            {
                this.playIncorrectPlacementSound();
                this.syllableSlider.showCorrectLine(correctPosition);
            }
            else
            {
                this.playSound("marking_this_is_correct_space");
                this.syllableSlider.moveToCorrectPosition(correctPosition);
            }
        }
    }
    
    private handleWordAudioComplete = ():void =>
    {
        setTimeout(() =>
        {
            if(this.interactionCompleteCallback)
            {
                this.interactionCompleteCallback(this.totalCorrectClicks, this.totalClicks);
            }
        },
        1000);
    }

    private hasVowelConsonantEndingE(word:string):boolean
    {
        const regex = /[aeiou][^aeiou]e\b/;
        return regex.test(word);
    }

    private hideAllHoverVisuals():void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            this.decodingSpaces[i].hideHover();
        }
    }

    private hideBug():void
    {
        if(this.beetle)
        {
            this.beetle.visible = false;
        }
    }

    private hideHint():void
    {
        if(this.hintDecodingSpace)
        {
            this.hintDecodingSpace.hideScaffold();
        }
    }

    private hideIconButtons():void
    {
        let len = this.iconBtns.length;
        for(let i = 0; i < len; i++)
        {
            this.iconBtns[i].hide();
        }
    }

    private hideDecodingSpaces():void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            this.decodingSpaces[i].hide();
        }
    }

    private isUndesiredMark(code:string):boolean
    {
        if(code == MarkCodes.GUARDIAN_2_LETTER
            || code == MarkCodes.SELECTION_FOR_Y
            || code == MarkCodes.SPLIT_BLEND
            || code == MarkCodes.SYLLABLE_BEGIN
            || code == MarkCodes.SYLLABLE_END
            || code == MarkCodes.SYLLABLE_SPLIT
            || code == MarkCodes.UNDERLINE
            || code == MarkCodes.VOWEL_2_LETTER)
        {
            return true;
        }
        return false;
    }

    private markIsJG(index:number):boolean
    {
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position)
            {
                if(i+1 <= this.decodingCommands.length-1)
                {
                    if(this.decodingCommands[i].code == MarkCodes.RAINBOW_J && this.decodingCommands[i+1].code == MarkCodes.RAINBOW_J_GUARDIAN)
                    {
                        return true;
                    }
                }
            }
        }
        return false
    }

    private markIsSG(index:number):boolean
    {
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position)
            {
                if(i+1 <= this.decodingCommands.length-1)
                {
                    if(this.decodingCommands[i].code == MarkCodes.RAINBOW_S && this.decodingCommands[i+1].code == MarkCodes.RAINBOW_S_GUARDIAN)
                    {
                        return true;
                    }
                }
            }
        }
        return false
    }
    
    private markIsY(index:number):boolean
    {
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position)
            {
                if(this.decodingCommands[i].code == MarkCodes.VOWEL_2_LETTER)
                {
                    return true;
                }
            }
        }
        return false;
    }
    
    private moveBug(targetX:number):void
    {
        gsap.to(this.beetle, {duration:1, x:targetX, ease:"none", onComplete:this.handleMoveBugComplete});
    }
    
    private multiBtnMarksFoundBtm(index:number):boolean
    {
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position && this.decodingCommands[i].placement == Placement.BTM)
            {
                if(i+1 <= this.decodingCommands.length-1)
                {
                    if(this.decodingCommands[i].position == this.decodingCommands[i+1].position && this.decodingCommands[i+1].placement == Placement.BTM)
                    {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    private multiBtnMarksFoundTop(index:number):boolean
    {
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position && this.decodingCommands[i].placement == Placement.TOP)
            {
                if(i+1 <= this.decodingCommands.length-1)
                {
                    if(this.decodingCommands[i].position == this.decodingCommands[i+1].position && this.decodingCommands[i+1].placement == Placement.TOP)
                    {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    
    private nextButtonUp():void
    {
        //debugger;
        let targetBtnIndex:number = 0;
        let curPosition:number = this.lastClickedIconBtn.charPosition;
        let nextPosition:number = 0;
        const len = this.iconBtns.length;
        if(this.lastClickedIconBtn.placement == Placement.BTM)
        {
            targetBtnIndex = this.lastClickedIconBtn.id+1;
            nextPosition = curPosition+1;
            if(this.iconBtns[targetBtnIndex].placement == Placement.BTM && this.iconBtns[targetBtnIndex].charPosition == nextPosition && this.iconBtns[targetBtnIndex].hasTwoButtons)
            {
                this.lastClickedIconBtn = this.iconBtns[targetBtnIndex];
                this.showSecondIconBtn();
            }
        }
        else
        {
            targetBtnIndex = this.lastClickedIconBtn.id-1;
            nextPosition = curPosition-1;
            if(this.iconBtns[targetBtnIndex].placement == Placement.TOP && this.iconBtns[targetBtnIndex].charPosition == nextPosition && this.iconBtns[targetBtnIndex].hasTwoButtons && !this.iconBtns[targetBtnIndex].secondBtnShown)
            {
                this.lastClickedIconBtn = this.iconBtns[targetBtnIndex];
                this.lastClickedIconBtn.secondBtnShown = true;
                this.showSecondIconBtn();
            }
        }
    }

    private nextMarkIsSyllableBegin():boolean
    {
        if(this.decodingCommands[this.curCommandIndex].code == MarkCodes.SYLLABLE_BEGIN)
        {
            return true;
        }
        return false;
    }

    private onMarkFadedIn = ():void =>
    {
        if(this.previewMarks)
        {
            this.curPosition = this.decodingCommands[this.curCommandIndex].position;
            this.populateMarkSelectionOptions(this.populateCurrentMarkSelectionOptions());
            this.destroyMarkSelectionBtns();
            this.markSelectionAttempts = 0;
            this.createMarkSelectionBtns();
            return;
        }
        if(this.curCommandIndex < this.decodingCommands.length)
        {
            this.curPosition = this.decodingCommands[this.curCommandIndex].position;
            if(this.decodingCommands[this.curCommandIndex].code == MarkCodes.SYLLABLE_BEGIN)
            {
                this.nonSyllabicPhaseComplete = true;
                let animationStart:number = 0;
                if(this.beetleAnimation)
                {
                    animationStart = this.beetleAnimation.progress();
                }
                this.animateBeetleAlongPath(this.curAnimationDuration, 0, animationStart, 1);
            }
            else
            {
                this.populateMarkSelectionOptions(this.populateCurrentMarkSelectionOptions());
                this.destroyMarkSelectionBtns();
                this.markSelectionAttempts = 0;
                this.createMarkSelectionBtns();
            }
        }
        else
        {
            this.hideAllHoverVisuals();
        }
    };

    private onSecondIconBtnHidden = (btnHidden:InteractionDecodeIconButtonGroup):void =>
    {
        btnHidden.visible = false;
    }
    
    private onSoundEnd = ():void =>
    {
        if(!this.instructionsPlayed)
        {
            this.instructionsPlayed = true;
            setTimeout(() =>
            {
                this.playSound("marking_choose_space_first_mark", this.onSoundEnd);
            }, 700);
            return;
        }
        if(!this.hintShown && this.firstInteraction)
        {
            this.startHintTimer();
        }
    };

    private playFirstHintAudio = ():void =>
    {
        this.hintShown = true;
        this.hintTimer.reset();
        this.playSound("marking_choose_space_first_mark");
    };
    
    private playSelectionHintAudio = ():void =>
    {
        this.selectionHintPlayed = true;
        this.selectionTimer.reset();
        this.playSound("marking_choose_correct_mark");
    }
    
    private playIncorrectPlacementSound():void
    {
        const correctBtn = this.getNextCorrectIconBtn();
        const correctIndex = correctBtn.id;
        let chars:string;
        switch(this.decodingCommands[this.curCommandIndex].code)
        {
            /*
                MarkCodes.AI_SOUND_OF_LONG_A,
                MarkCodes.AR_SOUND_OF_AIR,
                MarkCodes.CONSONANT_Y,
                MarkCodes.EA_OR_EI_SOUND_OF_LONG_A,
                MarkCodes.ER_SOUND_OF_EAR,
                MarkCodes.OW_SOUND_OF_LONG_O,
                MarkCodes.S_SOUND_OF_Z,
                MarkCodes.SCHWA,
                MarkCodes.SCHWA_LE_ENDINGS,
                MarkCodes.SELECTION_FOR_Y,
                MarkCodes.SOUND_OF_LONG_A,
                MarkCodes.SOUND_OF_Y_LONG_E,
                MarkCodes.SPLIT_BLEND,
                MarkCodes.SYLLABLE_BEGIN,
                MarkCodes.SYLLABLE_END,
                MarkCodes.SYLLABLE_SPLIT,
                MarkCodes.VOWEL_2_LETTER,
                MarkCodes.VOWEL_EXCEPTION,
                MarkCodes.VOWEL_EXCEPTION_SHORT_SOUND
             
             */
            /*
                marking_syllable_divide
                marking_syllable_long_to_short_vowel
                marking_vowel_spells_dot_sound
             */
            case MarkCodes.AR_SOUND_OF_OR:
            case MarkCodes.OR_SOUND_OF_ER:
                this.playSound("marking_r_controlled_vowel");
                break;
            case MarkCodes.BLEND_2_LETTER:
                chars = this.curSplitText.textFields[correctIndex].text;
                if(correctIndex+1 < this.curSplitText.textFields.length)
                {
                    chars += this.curSplitText.textFields[correctIndex+1].text;
                }
                if(chars == "ch" || chars == "sh" || chars == "wh" || chars == "th" || chars == "ph")
                {
                    this.playSound("marking_digraph");
                }
                else if(chars == "qu")
                {
                    this.playSound("marking_qu_combination");
                }
                else if(chars == "ck")
                {
                    this.playSound("marking_letters_ck");
                }
                else if(chars == "ar" || chars == "or" || chars == "er" || chars == "ir" || chars == "ur")
                {
                    this.playSound("marking_vowels_letter_r");
                }
                else if(chars == "au" || chars == "aw" || chars == "ou" || chars == "ow" || chars == "oi" || chars == "oy" || chars == "oo")
                {
                    this.playSound("marking_special_vowel_sound");
                }
                else
                {
                    this.playSound("marking_blend");
                }
                break;
            case MarkCodes.BLEND_3_LETTER:
                chars = this.curSplitText.textFields[correctIndex].text;
                if(correctIndex+1 < this.curSplitText.textFields.length)
                {
                    chars += this.curSplitText.textFields[correctIndex+1].text;
                }
                if(correctIndex+2 < this.curSplitText.textFields.length)
                {
                    chars += this.curSplitText.textFields[correctIndex+2].text;
                }
                if(chars == "ell" || chars == "ill" || chars == "ull" || chars == "all" || chars == "oll")
                {
                    this.playSound("marking_double_l_combination");
                }
                else if(chars == "ong" || chars == "ung" || chars == "ang" || chars == "ing" || chars == "onk" || chars == "unk" || chars == "ank" || chars == "ink")
                {
                    this.playSound("marking_glued_sound");
                }
                else if(chars == "tch" || chars == "dge")
                {
                    this.playSound("marking_trigraph");
                }
                else if(chars == "shr" || chars == "thr" || chars == "phl" || chars == "phr" || chars == "sph" || chars == "chl" || chars == "chr" || chars == "sch")
                {
                    this.playSound("marking_digraph_blend");
                }
                else if(chars == "ear")
                {
                    this.playSound("marking_sound_r_controlled_vowel_spelling");
                }
                else
                {
                    this.playSound("marking_three_letter_blend");
                }
                break;
            case MarkCodes.GUARDIAN:
            case MarkCodes.GUARDIAN_2_LETTER:
                this.playSound("marking_guardian_consonants");
                break;
            case MarkCodes.LONG:
            case MarkCodes.SHORT:
                this.playSound("marking_sound_vowel_spells");
                break;
            case MarkCodes.PH_MAKES_SOUND_OF_F:
                this.playSound("marking_sound_digraph_ph");
                break;
            case MarkCodes.RAINBOW_J:
                this.playSound("marking_rainbow_j");
                break;
            case MarkCodes.RAINBOW_S:
                this.playSound("marking_rainbow_s");
                break;
            case MarkCodes.SILENT_VOWEL:
                const word = this.curSplitText.text;
                if(this.hasVowelConsonantEndingE(word))
                {
                    this.playSound("marking_silent_e");
                }
                else
                {
                    this.playSound("marking_silent_letter");
                }
                break;
            case MarkCodes.SLIDE_ARROW:
                this.playSound("marking_slide");
                break;
            case MarkCodes.SOUND_OF_Y_LONG_I:
            case MarkCodes.SOUND_OF_Y_SHORT_I:
                this.playSound("marking_sound_vowel_y_spelling");
                break;
            case MarkCodes.UNDERLINE:
                chars = this.curSplitText.textFields[correctIndex].text;
                if(correctIndex+1 < this.curSplitText.textFields.length)
                {
                    chars += this.curSplitText.textFields[correctIndex+1].text;
                }
                if(correctIndex+2 < this.curSplitText.textFields.length)
                {
                    chars += this.curSplitText.textFields[correctIndex+2].text;
                }
                if(chars == "s" || chars == "es")
                {
                    this.playSound("marking_plural");
                }
                else if(chars == "ed" || chars == "ing" || chars == "er" || chars == "est")
                {
                    this.playSound("marking_suffix");
                }
                else if(chars == "old" || chars == "ost" || chars == "olt" || chars == "ind" || chars == "ild")
                {
                    this.playSound("marking_vowel_family");
                }
                break;
            case MarkCodes.VOICED_TH:
                this.playSound("marking_voiced_digraph");
                break;
            case MarkCodes.VOICELESS_TH:
                this.playSound("marking_voiceless_digraph");
                break;
            case MarkCodes.VOWEL:
                this.playSound("marking_vowel");
                break;
            case MarkCodes.VOWEL_SHORT_O_SOUND:
            case MarkCodes.VOWEL_EXCEPTION_SHORT_SOUND:
                this.playSound("marking_sound_vowel_spelling");
                break;
            default:
                this.playSound("marking_try_again");
        }
    }

    private playSound(id:string, completeCallback?:CompleteCallback):void
    {
        //debugger;
        const file = `${CDN}audio/student/marking/${id}.mp3`;
        this.soundManager.playSound(file, {completeCallback:completeCallback});
    }

    private populateCurrentMarkSelectionOptions():Array<string>
    {
        let options:Array<string> = [];
        const curCode:string = this.decodingCommands[this.curCommandIndex].code;
        options.push(curCode);
        switch(curCode)
        {
            case MarkCodes.VOWEL:
                options.push(MarkCodes.BLEND_2_LETTER);
                options.push(MarkCodes.GUARDIAN);
                options.push(MarkCodes.LONG);
                break;
            case MarkCodes.GUARDIAN:
            case MarkCodes.GUARDIAN_2_LETTER:
            case MarkCodes.GUARDIAN_3_LETTER:
            case MarkCodes.RAINBOW_J_GUARDIAN:
            case MarkCodes.RAINBOW_S_GUARDIAN:
                options.push(MarkCodes.VOWEL);
                options.push(MarkCodes.BLEND_2_LETTER);
                options.push(MarkCodes.LONG);
                break;
            case MarkCodes.BLEND_2_LETTER:
            case MarkCodes.BLEND_3_LETTER:
            case MarkCodes.BLEND_4_LETTER:
                options.push(MarkCodes.VOWEL);
                options.push(MarkCodes.GUARDIAN);
                options.push(MarkCodes.LONG);
                break;
            case MarkCodes.SHORT:
                options.push(MarkCodes.LONG);
                options.push(MarkCodes.VOWEL);
                options.push(MarkCodes.GUARDIAN);
                break;
            case MarkCodes.VOWEL_2_LETTER:
                options.push(MarkCodes.BLEND_2_LETTER);
                options.push(MarkCodes.GUARDIAN);
                options.push(MarkCodes.LONG);
                break;
            default:
                options.push(MarkCodes.BLEND_2_LETTER);
                options.push(MarkCodes.GUARDIAN);
                options.push(MarkCodes.VOWEL);
        }

        return options;
    }

    private populateDecodeCommands():void
    {
        this.markUtilily.populateDecodeCommands(this.options.code);
        this.decodingCommands = this.markUtilily.getDecodingCommands();
        this.curCommandIndex = 0;
        if(this.decodingCommands.length > 0)
        {
            this.curPosition = this.decodingCommands[this.curCommandIndex].position;
        }
    }

    private populateMarkSelectionOptions(options?:string[]):void
    {
        //debugger;
        if(options)
        {
            this.markSelectionOptions = options;
            this.markSelectionOptions = this.randomizeArray(this.markSelectionOptions);
        }
        else
        {
            this.markSelectionOptions = this.randomizeArray(this.markSelectionOptions);
            this.markSelectionOptions.unshift(this.decodingCommands[this.curCommandIndex].code);
            this.markSelectionOptions = this.removeDuplicates(this.markSelectionOptions);
            this.trimArrayLengthTo(this.markSelectionOptions, 4);
            this.sanitizeMarkSelectionOptions(this.decodingCommands[this.curCommandIndex].code);
            this.markSelectionOptions = this.randomizeArray(this.markSelectionOptions);
            //console.log(this.markSelectionOptions);
        }
    }

    private populateSyllableIndices():void
    {
        this.syllableIndices = [];
        const len = this.splitTextDefault.textFields.length;
        for(let i=0; i<len; i++)
        {
            if(i<len-1)
            {
                this.syllableIndices.push(i);
            }
        }
    }

    private populateSyllableSliderPoints():void
    {
        this.syllableSliderPoints = [];
        const baseX = this.splitTextDefault.x;
        const padding = 18;
        const len = this.syllableIndices.length;
        for(let i = 0; i < len; i++)
        {
            this.syllableSliderPoints.push((baseX-padding-(this.track2.x-this.track2.width/2))+this.splitTextDefault.textFields[i].x+this.splitTextDefault.textFields[i].width);
        }
    }

    private randomizeArray(array:any[]):any[]
    {
        let newArray:any[] = [];
        let len = array.length;
        let randomIndex:number;
        let curItem:any;
        for(let i = 0; i < len; i++)
        {
            randomIndex = Math.floor(Math.random()*array.length);
            curItem = array.splice(randomIndex, 1)[0];
            newArray.push(curItem);
        }
        return newArray;
    }

    private removeDuplicates(targetArray:string[]):string[]
    {
        let newArray:string[] = [];
        let len = targetArray.length;
        let curItem:string;
        for(let i = 0; i < len; i++)
        {
            curItem = targetArray[i];

            if(newArray.indexOf(curItem) == -1)
            {
                newArray.push(curItem);
            }
        }
        return newArray;
    }

    private removeIconBtnListeners(btn:InteractionDecodeIconButtonGroup):void
    {
        btn.onpointerdown = null;
        btn.onpointerout = null;
        btn.onpointerover = null;
    }
    
    private replayInstructionAudioFinished = ():void =>
    {
        if(!this.hintShown && this.hintTimer)
        {
            this.hintTimer.resume();
        }
    }

    private resetDecodingSpaces():void
    {
        let len = this.decodingSpaces.length;
        for(let i = 0; i < len; i++)
        {
            this.decodingSpaces[i].hideHover();
        }
    }

    private sanitizeMarkSelectionOptions(code:string):void
    {
        let codeToFind:string;
        if(code == MarkCodes.GUARDIAN)
        {
            this.searchForDuplicateMarksWithDifferentCodes(code, MarkCodes.GUARDIAN_2_LETTER);
        }
    }

    private searchForDuplicateMarksWithDifferentCodes(code:string, codeToFind:string):void
    {
        const len = this.markSelectionOptions.length;
        for(let i:number = 0; i < len; i++)
        {
            if(this.markSelectionOptions[i] == codeToFind)
            {
                const oldCode = codeToFind;
                this.markSelectionOptions.splice(i, 1);
                const marks = MarkCodes.MARKS.slice();
                const newCode = marks[Math.floor(Math.random()*marks.length)];
                this.markSelectionOptions.push(newCode);
                if(newCode == code || newCode == codeToFind)
                {
                    this.markSelectionOptions.pop();
                    this.markSelectionOptions.push(oldCode);
                    this.sanitizeMarkSelectionOptions(code);
                }
            }
        }
    }

    private setDefaultMarkOptions():void
    {
        this.markSelectionOptions = [];
        const allMarks = MarkCodes.MARKS.slice();
        const len = allMarks.length;
        for(let i:number = 0; i < len; i++)
        {
            if(this.isUndesiredMark(allMarks[i]))
            {
                continue;
            }
            this.markSelectionOptions.push(allMarks[i]);
        }
    }

    private setupMultiSyllabic():void
    {
        this.createSyllableSlider();
        this.createSyllableCheckBtn();
    }

    private setupTrigraphDGEPhaseTwo():void
    {
        this.disableIconButtons();
        this.lastClickedIconBtn.usable = false;
        this.lastClickedIconBtn.disable();
        this.removeIconBtnListeners(this.lastClickedIconBtn);
        gsap.to(this.lastClickedIconBtn, {alpha:0, duration:1, onComplete:()=>{this.lastClickedIconBtn.visible = false;}});
        gsap.to(this.decodingSpaces[this.lastClickedIconBtn.id], {alpha:0, duration:1});
        const len = this.trigraphDGEButtons.length;
        for(let i = 0; i < len; i++)
        {
            this.trigraphDGEButtons[i].alpha = 0;
            this.trigraphDGEButtons[i].visible = true;
            gsap.to(this.trigraphDGEButtons[i], {alpha:1, delay:1, duration:1});
            this.trigraphDGEDecodingSpaces[i].alpha = 0;
            this.trigraphDGEDecodingSpaces[i].visible = true;
            if(i == len-1)
            {
                gsap.to(this.trigraphDGEDecodingSpaces[i], {alpha:1, delay:1, duration:1, onComplete:() => {this.enableIconButtons();}});
            }
            else
            {
                gsap.to(this.trigraphDGEDecodingSpaces[i], {alpha:1, delay:1, duration:1});
            }
        }
    }

    private showCorrectScaffold():void
    {
        //debugger;
        this.hintDecodingSpace = this.getCorrectDecodingSpace();
        this.hintDecodingSpace.showScaffold();
    }
    
    private showSecondIconBtn():void
    {
        //debugger;
        this.lastClickedIconBtn.showSecondButton();
        this.decodingSpaces[this.lastClickedIconBtn.id].showSecondShape();
        const placement = this.lastClickedIconBtn.placement;
        if(this.lastClickedIconBtn.positionsMerged > 1)
        {
            const len = this.iconBtns.length;
            for(let i = 0; i < len; i++)
            {
                if(this.iconBtns[i] == this.lastClickedIconBtn)
                {
                    for(let j = 1; j < this.lastClickedIconBtn.positionsMerged; j++)
                    {
                        this.iconBtns[i+j].usable = false;
                        this.iconBtns[i+j].disable();
                        this.removeIconBtnListeners(this.iconBtns[i+j]);
                        gsap.to(this.iconBtns[i+j], {duration:.75, alpha:0, ease:"power2.out", onComplete:this.onSecondIconBtnHidden, onCompleteParams:[this.iconBtns[i+j]]});
                        gsap.to(this.decodingSpaces[i+j], {duration:.75, alpha:0, ease:"power2.out"});
                    }
                    /*if(placement == Placement.TOP)
                    {
                        Delay.callLater(()=>
                        {
                            const merged = this.lastClickedIconBtn.positionsMerged;
                            const targetIndex = (this.lastClickedIconBtn.index+1)-merged;
                            this.lastClickedIconBtn.x = this.iconBtns[targetIndex].x;
                            this.decodingSpaces[i].x = this.lastClickedIconBtn.x;
                        }, 700);
                    }*/
                    return;
                }
            }
        }
    }

    private specialVowelFound(code:string, index:number):boolean
    {
        //debugger;
        if(code == MarkCodes.VOWEL)
        {
            const nextMarkIndex = this.curCommandIndex+1;
            if(index < this.iconBtns.length && nextMarkIndex < this.decodingCommands.length)
            {
                if(this.iconBtns[index].placement == Placement.BTM && this.decodingCommands[nextMarkIndex].code == MarkCodes.UNDERLINE)
                {
                    return true;
                }
            }
        }
        return false;
    }

    private startHintTimer():void
    {
        this.hintTimer = new Timer(5000, 1);
        this.hintTimer.addCustomEventListener(TimerEvent.TIMER_COMPLETE, this.playFirstHintAudio);
        this.hintTimer.start();
    }
    
    private startSelectionTimer():void
    {
        this.selectionTimer = new Timer(5000, 1);
        this.selectionTimer.addCustomEventListener(TimerEvent.TIMER_COMPLETE, this.playSelectionHintAudio);
        this.selectionTimer.start();
    }

    private stopHintTimer():void
    {
        this.hintShown = true;
        if(this.hintTimer)
        {
            this.hintTimer.reset();
        }
    }

    private stopSelectionTimer():void
    {
        this.selectionHintPlayed = true;
        if(this.selectionTimer)
        {
            this.selectionTimer.reset();
        }
    }

    private threeCharMarkFoundBtm(index:number):boolean
    {
        let found:boolean = false;
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingCommands[i].position == index+1 && (this.decodingCommands[i].code == MarkCodes.BLEND_3_LETTER  ||
                this.decodingCommands[i].code == MarkCodes.UNDERLINE))
            {
                found = true;
                break;
            }
        }
        return found;
    }

    private threeCharMarkFoundTop(index:number):boolean
    {
        let found:boolean = false;
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingCommands[i].position == index+1 && this.decodingCommands[i].code == MarkCodes.GUARDIAN_3_LETTER)
            {
                found = true;
                break;
            }
        }
        return found;
    }

    private trigraphDGEFound(index:number):boolean
    {
        //debugger;
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(index == this.decodingCommands[i].position)
            {
                if(this.decodingCommands[i].code == MarkCodes.BLEND_3_LETTER)
                {
                    const eIndex = this.decodingCommands[i].position+1;
                    if(eIndex < this.splitTextDefault.textFields.length && this.splitTextDefault.textFields[eIndex].text == "e")
                    {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    
    private trimArrayLengthTo(targetArray:any[], targetLength:number):void
    {
        while(targetArray.length > targetLength)
        {
            targetArray.pop();
        }
    }

    private twoCharMarkFoundBtm(index:number):boolean
    {
        let found:boolean = false;
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingCommands[i].position == index+1 && (this.decodingCommands[i].code == MarkCodes.BLEND_2_LETTER ||
                this.decodingCommands[i].code == MarkCodes.VOICED_TH ||
                this.decodingCommands[i].code == MarkCodes.VOICELESS_TH))
            {
                found = true;
                break;
            }
        }
        return found;
    }

    private twoCharMarkFoundTop(index:number):boolean
    {
        let found:boolean = false;
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(this.decodingCommands[i].position == index+1 &&
                (this.decodingCommands[i].code == MarkCodes.GUARDIAN_2_LETTER ||
                    this.decodingCommands[i].code == MarkCodes.PH_MAKES_SOUND_OF_F ||
                    this.decodingCommands[i].code == MarkCodes.RAINBOW_J ||
                    this.decodingCommands[i].code == MarkCodes.RAINBOW_S ||
                    this.decodingCommands[i].code == MarkCodes.OR_SOUND_OF_ER))
            {
                found = true;
                break;
            }
        }
        return found;
    }
    
    private twoMarksTogetherAndOneIsMultiple(position:number, placement:string=Placement.BTM):boolean
    {
        /*if(placement == Placement.TOP)
        {
            debugger;
        }*/
        const len = this.decodingCommands.length;
        for(let i = 0; i < len; i++)
        {
            if(position == this.decodingCommands[i].position)
            {
                if(placement == Placement.BTM)
                {
                    if(i+1 <= this.decodingCommands.length-1)
                    {
                        if(position+1 == this.decodingCommands[i+1].position && this.decodingCommands[i-1].placement == placement)
                        {
                            return true;
                        }
                    }
                }
                else
                {
                    if(i-1 >= 0)
                    {
                        if(position+1 == this.decodingCommands[i-1].position &&
                            this.decodingCommands[i-1].placement == placement &&
                            (this.decodingCommands[i].code == MarkCodes.GUARDIAN_2_LETTER ||
                                this.decodingCommands[i].code == MarkCodes.PH_MAKES_SOUND_OF_F ||
                                this.decodingCommands[i].code == MarkCodes.RAINBOW_J ||
                                this.decodingCommands[i].code == MarkCodes.RAINBOW_S))
                        {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }
    
    private withinBy(testValue:number, targetValue:number, by:number = this.WITHIN_BY):boolean
    {
        return testValue >= targetValue-by && testValue <= targetValue+by;
    }

    public create():void
    {
        //this.options.code = "2x,3b";
        if(this.firstInteraction && !this.previewMarks && !this.isDebug && !this.isPrototype)
        {
            this.playSound("instructions", this.onSoundEnd);
        }
        this.doInitialCheckForProperCodeFormat();
        this.checkForMultiSyllabic();
        this.createBg();
        this.createWord();
        //debugger;
        this.populateDecodeCommands();
        if(this.decodingCommands.length == 0)
        {
            alert("The code for this word is not formatted properly. Please check the code and try again.");
            return;
        }
        if(this.markSelectionOptions == null)
        {
            this.setDefaultMarkOptions();
            this.populateMarkSelectionOptions(this.populateCurrentMarkSelectionOptions());
        }
        //debugger;
        this.decodingSpaces = [];
        this.iconBtns = [];
        this.createDecodingSpaces(Placement.BTM, Direction.UP);
        this.createIconButtons("", Placement.BTM);
        if(this.isMultiSyllabic)
        {
            this.createDecodingSpaces(Placement.TOP, Direction.DOWN, false);
            this.createIconButtons("", Placement.TOP, this.iconBtns.length, false);
        }
        else
        {
            this.createDecodingSpaces(Placement.TOP, Direction.DOWN);
            this.createIconButtons("", Placement.TOP, this.iconBtns.length);
        }
        this.createMotionPaths();
        if(!this.previewMarks)
        {
            this.createBeetle();
        }
        this.createMarkSelectionBtns();
        if(this.isMultiSyllabic)
        {
            this.populateSyllableIndices();
            this.populateSyllableSliderPoints();
            //this.hideIconButtons();
            //this.hideDecodingSpaces();
            //this.hideBug();
            this.setupMultiSyllabic();
        }
        if(this.isNonsense)
        {
            this.createNonSenseMark();
        }

        if(this.previewMarks)
        {
            this.markUtilily.createAndDisplayAllMarks(this.curSplitText);
        }

        // ------------------ TEST SELECTION BTN ------------------
        /*const testMark = MarkCodes.SOUND_OF_Y_LONG_E;
        this.decodingCommands[this.curCommandIndex].code = testMark;
        this.destroyMarkSelectionBtns();
        this.populateMarkSelectionOptions(this.populateCurrentMarkSelectionOptions());
        this.createMarkSelectionBtns();
        this.createSampleMarkSelectionBtn(testMark);*/
        // ------------------ TEST SELECTION BTN ------------------

        //debugger;
        //this.animateBeetleAlongPath();
        //this.createSampleMark(MarkCodes.BLEND_2_LETTER);
        //this.createSampleMark(MarkCodes.VOWEL);
        //this.createSampleMark(MarkCodes.AR_SOUND_OF_OR);
        //this.createSampleMark(MarkCodes.CONSONANT_Y);
        //this.createSampleMark(MarkCodes.OR_SOUND_OF_ER);
        //this.createSampleMark(MarkCodes.ER_SOUND_OF_EAR);
        //this.createSampleMark(MarkCodes.RAINBOW_J);
        //this.createSampleMark(MarkCodes.RAINBOW_S);
        //this.createSampleMark(MarkCodes.SCHWA);
        //this.createSampleMark(MarkCodes.SHORT);
        //this.createSampleMark(MarkCodes.VOWEL_SHORT_O_SOUND);
        //this.createSampleMark(MarkCodes.SILENT_VOWEL);
        //this.createSampleMark(MarkCodes.SLIDE_ARROW);
        //this.createSampleMark(MarkCodes.SOUND_OF_LONG_A);
        //this.createSampleMark(MarkCodes.OW_SOUND_OF_LONG_O);
        //this.createSampleMark(MarkCodes.EA_SOUND_OF_SHORT_E_EA);
        //this.createSampleMark(MarkCodes.PH_MAKES_SOUND_OF_F);
        //this.createSampleMark(MarkCodes.SOUND_OF_K);
        //this.createSampleMark(MarkCodes.S_SOUND_OF_Z);
        //this.createSampleMark(MarkCodes.SOUND_OF_SH);
        //this.createSampleMark(MarkCodes.SOUND_OF_Y_LONG_E);
        //this.createSampleMark(MarkCodes.SOUND_OF_Y_LONG_I);
        //this.createSampleMark(MarkCodes.SOUND_OF_Y_SHORT_I);
        //this.createSampleMark(MarkCodes.UNDERLINE);
        //this.createSampleMark(MarkCodes.VOICED_TH);
        //this.createSampleMark(MarkCodes.VOICELESS_TH);
        //this.createSampleMark(MarkCodes.VOWEL_EXCEPTION);
        //this.createSampleMark(MarkCodes.VOWEL_EXCEPTION_SHORT_SOUND);
    }

    public override destroy():void
    {
        if(this.curSound)
        {
            this.curSound.stop();
        }
        DecodingModel.getInstance().curDecodingString = "";
        this.destroyTimers();
        this.destroyDecodingSpaces();
        this.destroyIconButtons();
        this.destroyBeetleAnimation();
        this.removeChildren();
        
        this.animationStop = null;
        this.beetle = null;
        this.curSound = null;
        this.curSplitText = null;
        this.decodingCommands = null;
        this.decodingModel = null;
        this.decodingSpaces = null;
        this.door = null;
        this.hintDecodingSpace = null;
        this.iconBtns = null;
        this.lastClickedIconBtn = null;
        this.lastHoveredDecodingSpace = null;
        this.mainBg = null;
        this.markSelectionBtns = null;
        this.markSelectionBtnsContainer = null;
        this.markSelectionOptions = null;
        this.markUtilily = null;
        this.model = null;
        this.motionPaths = null;
        this.options = null;
        this.soundManager = null;
        this.splitTextDefault = null;
        this.syllableIndices = null;
        this.syllableSlider = null;
        this.syllableSliderPoints = null;
        this.totalClicks = null;
        this.totalCorrectClicks = null;
        this.track = null;
        this.track2 = null;
        this.vines = null;
        
        super.destroy();
    }

    public replayInstructionAudio():void
    {
        if(!this.hintShown && this.hintTimer)
        {
            this.hintTimer.pause();
        }
        this.soundManager.stopLastSound();
        this.playSound("instructions", this.replayInstructionAudioFinished);
    }
}